/*  ============================================================================
    TeamReportingSettings.jsx — the Team Reporting roster and targets.
    Settings → "Team Reporting — Users & Targets".

    ⚠ INTERNAL ONLY. This decides who appears in a per-person performance view
      and who is marked on leave. Never client-facing.

    Two flags per Autotask resource, keyed by resourceId:

      Report on — is this person in the Team Reporting roster at all?
      Active    — currently working, or ON LEAVE / paused?

    ⚠ AUTOTASK'S OWN `isActive` IS NOT "ON LEAVE" and is shown READ-ONLY beside
      ours for exactly that reason. It means licensed and enabled: somebody on
      maternity leave is still a perfectly active Autotask user. What it IS good
      for is showing a departed person, so they can be un-reported rather than
      queried forever.

    ⚠ WHY A SEPARATE COMPONENT, not another block inside MspOsApp.jsx: that file
      is 16k lines and is being edited by other work in parallel. A self-contained
      component costs the settings form one line and keeps this reviewable.
    ============================================================================ */

import { useState, useEffect, useCallback } from "react";
import apiClient from "../api/apiClient";

const errMsg = (e) => (e?.response?.status ? `HTTP ${e.response.status}` : (e?.message || "request failed"));

const TARGETS = [
  { key: "firstResponseHours",   label: "First response",      unit: "hours",      step: "0.5",
    hint: "Share of first responses at or under this counts as met." },
  { key: "ticketsClosedPerDay",  label: "Tickets closed",      unit: "per day",    step: "0.5",
    hint: "One target across L1/L2/L3 mis-reads by design — hard tickets close slower." },
  { key: "itGlueTouchesPerWeek", label: "IT Glue touches",     unit: "per week",   step: "1",
    hint: "Creates + updates in the activity log." },
  { key: "timesheetHoursPerDay", label: "Timesheet",           unit: "hours/day",  step: "0.1",
    hint: "The expected LOGGED day. Not the 8.5h business-hours window." },
];

export default function TeamReportingSettings() {
  const [roster, setRoster] = useState(null);
  const [targets, setTargets] = useState(null);
  const [dirty, setDirty] = useState({});          // resourceId -> {reportOn, active}
  const [targetsDirty, setTargetsDirty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  const load = useCallback((refresh) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    setError(null);
    Promise.all([
      apiClient.get(`/api/team-reporting/roster${refresh ? "?refresh=1" : ""}`, { timeout: 45000 }),
      apiClient.get("/api/team-reporting/config", { timeout: 45000 }),
    ])
      .then(([r, c]) => {
        setRoster(r.data?.roster || []);
        setTargets(c.data?.config?.targets || {});
        setDirty({});
        setTargetsDirty(null);
      })
      .catch((e) => setError(errMsg(e)))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, []);

  useEffect(() => { load(false); }, [load]);

  const rowValue = (r, field) =>
    (dirty[r.resourceId] && dirty[r.resourceId][field] !== undefined)
      ? dirty[r.resourceId][field]
      : r[field];

  const toggle = (r, field) => {
    const next = !rowValue(r, field);
    setDirty((d) => ({ ...d, [r.resourceId]: { ...(d[r.resourceId] || {}), [field]: next } }));
    setSaved(null);
  };

  const targetValue = (key) =>
    (targetsDirty && targetsDirty[key] !== undefined) ? targetsDirty[key] : (targets?.[key] ?? "");

  const setTarget = (key, value) => {
    setTargetsDirty((t) => ({ ...(t || {}), [key]: value }));
    setSaved(null);
  };

  const hasChanges = Object.keys(dirty).length > 0 || !!targetsDirty;

  /* Roster and targets save together — they are one form, and two buttons on
     one form is how half a change gets left behind. */
  const save = useCallback(() => {
    setSaving(true);
    setError(null);
    const calls = [];
    if (Object.keys(dirty).length) {
      calls.push(apiClient.put("/api/team-reporting/roster", { roster: dirty }, { timeout: 45000 }));
    }
    if (targetsDirty) {
      const clean = {};
      Object.entries(targetsDirty).forEach(([k, v]) => {
        const n = Number(v);
        if (v !== "" && Number.isFinite(n)) clean[k] = n;
      });
      if (Object.keys(clean).length) {
        calls.push(apiClient.put("/api/team-reporting/config", { targets: clean }, { timeout: 45000 }));
      }
    }
    Promise.all(calls)
      .then(() => { setSaved(new Date()); load(false); })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setSaving(false));
  }, [dirty, targetsDirty, load]);

  const Check = ({ on, onClick, title }) => (
    <span onClick={onClick} title={title} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      style={{
        display: "inline-block", width: 17, height: 17, borderRadius: 4, cursor: "pointer",
        border: `1px solid ${on ? "var(--ops)" : "var(--border)"}`,
        background: on ? "var(--ops)" : "white", color: "white",
        textAlign: "center", lineHeight: "15px", fontSize: 11, userSelect: "none",
      }}>{on ? "✓" : ""}</span>
  );

  const reportedOn = (roster || []).filter((r) => rowValue(r, "reportOn"));
  const onLeaveCount = reportedOn.filter((r) => !rowValue(r, "active")).length;

  return (
    <div style={{ background: "white", border: "1px solid var(--border)", borderRadius: 8, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, fontWeight: 700 }}>Team Reporting — Users &amp; Targets</div>
        <button className="btn" style={{ fontSize: 10, padding: "4px 9px" }}
          onClick={() => load(true)} disabled={loading || refreshing || saving}>
          {refreshing ? "Refreshing…" : "↻ Refresh from Autotask"}
        </button>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {saved && !hasChanges && <span style={{ fontSize: 9.5, color: "var(--ops)" }}>Saved</span>}
          <button className="btn btn-S" style={{ fontSize: 10, padding: "5px 12px" }}
            onClick={save} disabled={!hasChanges || saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <div style={{ fontSize: 9.5, color: "var(--text3)", marginBottom: 10, lineHeight: 1.5 }}>
        Internal only. <strong>Report on</strong> decides who appears in Team Reporting at all —
        opt people in deliberately. <strong>Active</strong> is ours: untick it for someone on leave and
        they are labelled “On leave”, kept out of the team averages and never red-flagged, rather than
        showing as a row of zeros. Autotask’s own status is shown read-only — it means <em>licensed and
        enabled</em>, not on leave, so someone on parental leave still reads as active there.
      </div>

      {error && (
        <div style={{ padding: "8px 12px", background: "var(--danger-d)", border: "1px solid rgba(231,76,60,0.25)", borderRadius: 6, fontSize: 10.5, color: "var(--danger)", marginBottom: 10 }}>
          {error}
        </div>
      )}

      {loading && !roster ? (
        <div style={{ fontSize: 10.5, color: "var(--text3)", padding: "10px 0" }}>Loading Autotask resources…</div>
      ) : (
        <>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--text3)", fontSize: 9, textTransform: "uppercase", letterSpacing: ".08em" }}>
                <th style={{ padding: "5px 8px 5px 0" }}>Name</th>
                <th style={{ padding: "5px 8px" }}>Email</th>
                <th style={{ padding: "5px 8px", whiteSpace: "nowrap" }}>Autotask status</th>
                <th style={{ padding: "5px 8px", textAlign: "center", whiteSpace: "nowrap" }}>Report on</th>
                <th style={{ padding: "5px 0 5px 8px", textAlign: "center" }}>Active</th>
              </tr>
            </thead>
            <tbody>
              {(roster || []).map((r) => {
                const on = rowValue(r, "reportOn");
                const active = rowValue(r, "active");
                return (
                  <tr key={r.resourceId} style={{ borderTop: "1px solid var(--border)", opacity: on ? 1 : 0.55 }}>
                    <td style={{ padding: "6px 8px 6px 0", fontWeight: on ? 600 : 400 }}>
                      {r.name}
                      {on && !active && (
                        <span style={{ marginLeft: 6, fontSize: 8.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--cal)" }}>on leave</span>
                      )}
                    </td>
                    <td style={{ padding: "6px 8px", color: "var(--text3)" }}>{r.email || "—"}</td>
                    <td style={{ padding: "6px 8px" }}>
                      {/* Read-only, and deliberately so — this is Autotask's fact, not ours. */}
                      <span style={{ fontSize: 9.5, color: r.autotaskActive ? "var(--text3)" : "var(--danger)", fontWeight: r.autotaskActive ? 400 : 700 }}>
                        {r.autotaskActive ? "Active" : "Disabled — departed?"}
                      </span>
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "center" }}>
                      <Check on={on} onClick={() => toggle(r, "reportOn")}
                        title="Include this person in Team Reporting" />
                    </td>
                    <td style={{ padding: "6px 0 6px 8px", textAlign: "center" }}>
                      <Check on={active} onClick={() => toggle(r, "active")}
                        title="Untick for someone on leave — labelled, and left out of the averages" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ fontSize: 9.5, color: "var(--text3)", marginTop: 8 }}>
            {reportedOn.length} reported on{onLeaveCount ? ` · ${onLeaveCount} on leave` : ""}.
          </div>

          {/* ── Targets, in the SAME form ─────────────────────────────────────
              One save for both: they are edited in the same sitting, and two
              buttons on one form is how half a change gets left behind. */}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 2 }}>Targets</div>
            <div style={{ fontSize: 9.5, color: "var(--text3)", marginBottom: 10, lineHeight: 1.5 }}>
              These drive the red/amber/green status. A target nobody can hit is not a standard, it is
              decoration — and a page that shows everyone red every week stops being read.
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              {TARGETS.map((t) => (
                <div key={t.key} style={{ flex: "1 1 200px", minWidth: 190 }}>
                  <label style={{ display: "block", fontSize: 9.5, color: "var(--text2)", fontWeight: 600, marginBottom: 3 }}>
                    {t.label} <span style={{ color: "var(--text3)", fontWeight: 400 }}>({t.unit})</span>
                  </label>
                  {/* text + inputMode, not type=number: a numeric field seeded
                      with a literal 0 appends rather than replaces, which is the
                      `010` bug the quoting fields already hit. */}
                  <input className="inp" type="text" inputMode="decimal" style={{ marginBottom: 3 }}
                    value={targetValue(t.key)}
                    onChange={(e) => setTarget(t.key, e.target.value.replace(/[^0-9.]/g, ""))} />
                  <div style={{ fontSize: 9, color: "var(--text3)", lineHeight: 1.4 }}>{t.hint}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
